import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { ArrowRight, X } from 'lucide-react'
import { ArrowLink, ButtonLink } from '@/components/generals/button'
import { LiveDot } from '@/components/generals/live-dot'
import { FlowTimeline } from '@/components/product/flow-timeline'

interface FlowPlayerDialogProps {
    open: boolean
    onClose: () => void
}

/** "Watch the flow": a modal that plays how a single shipment moves through Quayside. */
export const FlowPlayerDialog: FC<FlowPlayerDialogProps> = ({ open, onClose }): ReactNode => {
    const ref = useRef<HTMLDialogElement>(null)

    useEffect(() => {
        const dialog = ref.current

        if (!dialog) return

        if (open && !dialog.open) dialog.showModal()
        if (!open && dialog.open) dialog.close()
    }, [open])

    return (
        <dialog
            ref={ref}
            aria-labelledby='flow-player-title'
            onClose={onClose}
            onClick={event => {
                if (event.target === ref.current) onClose()
            }}
            className='m-auto max-h-[calc(100dvh-24px)] w-[min(1040px,calc(100vw-24px))] overflow-y-auto rounded-[24px] border border-ink/10 bg-paper p-0 sm:rounded-[32px] text-ink shadow-lift backdrop:bg-ink/45 backdrop:backdrop-blur-sm'
        >
            <div className='relative p-5 sm:p-10 lg:p-12'>
                <div className='flex items-start justify-between gap-4 sm:gap-6'>
                    <div>
                        <p className='eyebrow flex items-center gap-2 text-stone'>
                            <LiveDot />
                            How a flow runs
                        </p>

                        <h2 id='flow-player-title' className='headline mt-4 text-[clamp(1.9rem,4vw,3rem)]'>
                            From booking to proof of delivery.
                        </h2>

                        <p className='mt-3 max-w-xl text-[15px] leading-relaxed text-ink-soft'>
                            One shipment, Rotterdam to Chicago. Every system hears about it the moment it changes, and a person steps in when something does not add up.
                        </p>
                    </div>

                    <button
                        type='button'
                        onClick={onClose}
                        aria-label='Close'
                        className='grid size-10 shrink-0 place-items-center rounded-full border border-ink/15 text-ink transition-colors hover:bg-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine'
                    >
                        <X aria-hidden='true' className='size-5' />
                    </button>
                </div>

                <div className='mt-8 rounded-[20px] bg-cream p-5 sm:mt-12 sm:rounded-[24px] sm:p-8'>
                    {open && <FlowTimeline />}
                </div>

                <div className='mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center'>
                    <ButtonLink href='/contact' size='md'>
                        Book a walkthrough
                        <ArrowRight aria-hidden='true' className='size-4 transition-transform group-hover/btn:translate-x-0.5' />
                    </ButtonLink>

                    <ArrowLink href='/platform' className='sm:ml-4'>
                        See the whole platform
                    </ArrowLink>
                </div>
            </div>
        </dialog>
    )
}
